/**
 * ManualRiskSelector — the closed-vocabulary dropdown NTN staff uses to
 * assign the PM-facing risk banner on Employment / Landlord / Documents.
 *
 * The tone→color mapping mirrors VerifiedReport's BANNER_TONES so the
 * preview always matches what a PM will see once published.
 */
import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, AlertTriangle, ShieldAlert, HelpCircle } from "lucide-react";
import { toast } from "sonner";

const TONE = {
  green:  { bg: "bg-emerald-700", ring: "ring-emerald-500", chip: "bg-emerald-50 text-emerald-800 ring-emerald-200", Icon: ShieldCheck },
  amber:  { bg: "bg-amber-500",   ring: "ring-amber-400",   chip: "bg-amber-50 text-amber-800 ring-amber-200",       Icon: AlertTriangle },
  orange: { bg: "bg-orange-600",  ring: "ring-orange-500",  chip: "bg-orange-50 text-orange-800 ring-orange-200",     Icon: AlertTriangle },
  red:    { bg: "bg-rose-700",    ring: "ring-rose-500",    chip: "bg-rose-50 text-rose-800 ring-rose-200",           Icon: ShieldAlert },
  slate:  { bg: "bg-slate-500",   ring: "ring-slate-400",   chip: "bg-slate-50 text-slate-700 ring-slate-200",        Icon: HelpCircle },
};

/**
 * Standalone dialog. Fetches the closed track catalog on first open so no
 * label list is ever hard-coded on the frontend.
 *
 * props:
 *   open          — dialog open state
 *   onOpenChange  — dialog controller
 *   track         — "employment" | "landlord" | "documents"
 *   screeningId   — sid to POST to
 *   current       — current { tone, label, note? } (or null)
 *   onSaved       — callback(newBanner)
 */
export default function ManualRiskSelector({ open, onOpenChange, track, screeningId, current, onSaved }) {
  const [options, setOptions] = useState([]);
  const [label, setLabel] = useState(current?.label || "");
  const [note, setNote] = useState(current?.note || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLabel(current?.label || "");
    setNote(current?.note || "");
    (async () => {
      try {
        const { data } = await apiClient.get("/staff-ops/risk/track-catalog");
        setOptions(data.tracks[track] || []);
      } catch (e) {
        toast.error("Could not load the risk catalog.");
      }
    })();
  }, [open, track, current?.label, current?.note]);

  const save = async () => {
    if (!label) {
      toast.error("Choose a risk label.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await apiClient.post(
        `/staff-ops/case/${screeningId}/${track}/manual-risk`,
        { label, note: note || null },
      );
      toast.success("Manual risk saved.");
      onSaved?.(data.manual_status_banner);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the risk.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="manual-risk-selector">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Set PM-facing risk</DialogTitle>
          <DialogDescription>
            Pick the exact banner the Property Manager will see for this verification.
            Findings and internal indicators no longer set this — you do.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {options.map((o) => {
            const t = TONE[o.tone] || TONE.slate;
            const Icon = t.Icon;
            const selected = label === o.label;
            return (
              <button
                key={o.label}
                type="button"
                onClick={() => setLabel(o.label)}
                data-testid={`risk-opt-${o.tone}-${o.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`w-full text-left rounded-md border p-4 flex items-center gap-3 transition-all
                  ${selected ? `${t.bg} text-white border-transparent ring-2 ${t.ring}` : "border-slate-200 hover:border-slate-400"}`}
              >
                <Icon className="h-5 w-5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${selected ? "text-white/80" : "text-slate-500"}`}>
                    {o.tone}
                  </div>
                  <div className={`font-serif text-base leading-tight ${selected ? "text-white" : "text-slate-900"}`}>
                    {o.label}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-2">
          <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">Internal note (optional)</Label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Reasoning — retained on the risk history for audit."
            className="mt-1.5 rounded-sm"
            rows={3}
            data-testid="risk-note"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="risk-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !label}
                  className="bg-[#0A192F] hover:bg-[#0A192F]/90"
                  data-testid="risk-save">
            {busy ? "Saving…" : "Save risk"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
